import React, { useEffect, useState } from "react";
import { LogIn, LogOut, CheckCircle2, Clock, User } from "lucide-react";
import type { AttendanceRecord, AttendanceType, Karyawan } from "../../controller/mobile/types";
import { todayDateOnly, timeLabel, toDateOnly } from "../../controller/mobile/Dateutils";
import type { Page } from "../../controller/mobile/types";

interface HomePageProps {
  employee: Karyawan;
  records: AttendanceRecord[];
  onNavigate: (page: Extract<Page, "home" | "capture" | "history" | "izin" | "izinlist" | "lembur" | "lemburlist" | "profile" | "none">) => void;
  onStartCapture: (type: AttendanceType) => void;
} 
 
function greeting(hour: number): string {
  if (hour < 10) return "Selamat pagi";
  if (hour < 15) return "Selamat siang";
  if (hour < 18) return "Selamat sore";
  return "Selamat malam";
}
 
export default function HomePage({
  employee,
  records,
  onNavigate,
  onStartCapture,
}: HomePageProps): React.ReactElement {
  const [now, setNow] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const firstName = typeof employee?.nama === "string" ? employee.nama.trim().split(/\s+/)[0] : "";

  // const eD = employee;
 
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setIsLoading(records.length === 0);
  }, [records]);
   
  const todayRecord =
    records.find((r) => {
      const recordTime = r.jam_masuk ?? r.jam_keluar;
      return (
        recordTime !== null &&
        recordTime !== undefined &&
        toDateOnly(recordTime) === todayDateOnly() &&
        String(r.id_karyawan) === String(employee.id)
      );
    }) ?? null;

  const isIzinOrAlfa = todayRecord?.status === "izin" || todayRecord?.status === "alfa";
  const isPending = todayRecord?.isPending === true;
  const belumMasuk = !todayRecord;
  const sudahMasukBelumPulang = todayRecord && !todayRecord.jam_keluar && !isIzinOrAlfa;
  const sudahSelesai = todayRecord && todayRecord.jam_keluar && !isIzinOrAlfa;
 
  const timeString = now.toLocaleTimeString("id-ID", { hour12: false });
  const dateString = now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
 
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 rounded-b-3xl bg-slate-900 px-6 pb-6 pt-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium uppercase tracking-widest text-slate-400">
              {greeting(now.getHours())}, {firstName || "Karyawan"}
            </p>
            {employee.jabatan && (
              <p className="text-xs text-slate-400">
                {employee.jabatan}
                {employee.no_kary ? ` · ${employee.no_kary}` : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            className="ml-2 rounded-full bg-slate-700 p-2 transition-colors hover:bg-slate-600"
            title="Buka profil" onClick={ () => onNavigate("profile") }
          >
            <User className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 font-mono text-4xl font-semibold tabular-nums tracking-tight">
          {timeString}
        </p>
        <p className="mt-1 text-sm text-slate-300">{dateString}</p>
      </header>
 
      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 py-5">
        {isLoading ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Clock className="h-6 w-6 text-slate-500" />
            </div>
            <p className="text-sm font-medium text-slate-800">Memuat data absensi...</p>
          </section>
        ) : null}

        {!isLoading && belumMasuk && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Clock className="h-6 w-6 text-slate-500" />
            </div>
            <p className="text-sm font-medium text-slate-800">
              Kamu belum absen masuk hari ini
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Ambil foto selfie dan pastikan lokasi aktif untuk absen.
            </p>
            <button
              type="button"
              onClick={() => onStartCapture("masuk")}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              <LogIn className="h-4 w-4" />
              Absen Masuk
            </button>
          </section>
        )}
 
        {!isLoading && sudahMasukBelumPulang && todayRecord && (
          <section className="space-y-3">
            <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
              <div className="text-sm">
                <p className="font-medium text-emerald-800">
                  Absen masuk tercatat
                </p>
                <p className="text-emerald-700">
                  Jam {timeLabel(todayRecord.jam_masuk)}
                </p>
                {isPending && (
                  <p className="mt-1 text-[11px] text-slate-600">
                    Absen sudah tercatat secara lokal. Menunggu konfirmasi backend.
                  </p>
                )}
              </div>
            </div>
 
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
              <p className="text-sm font-medium text-slate-800">
                Sudah selesai kerja hari ini?
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Jangan lupa absen pulang sebelum meninggalkan kantor.
              </p>
              <button
                type="button"
                onClick={() => onStartCapture("pulang")}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                <LogOut className="h-4 w-4" />
                Absen Pulang
              </button>
            </div>
          </section>
        )}
 
        {!isLoading && isIzinOrAlfa && todayRecord && (
          <section className="space-y-3">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <p className="text-sm font-semibold text-amber-800">
                Status hari ini: {todayRecord.status === "izin" ? "Izin" : "Alfa"}
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Tidak ada absensi masuk/pulang yang dicatat untuk hari ini.
              </p>
            </div>
          </section>
        )}

        {!isLoading && sudahSelesai && todayRecord && (
          <section className="space-y-3">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-emerald-800">
                Absensi hari ini selesai
              </p>
              {isPending ? (
                <p className="mt-1 text-xs text-slate-600">
                  Absen pulang sudah tercatat secara lokal dan sedang disinkronkan.
                </p>
              ) : (
                <p className="mt-1 text-xs text-emerald-700">
                  Sampai jumpa besok!
                </p>
              )}
            </div>
 
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs text-slate-500">Masuk</p>
                <p className="mt-1 font-mono text-lg font-semibold text-slate-800">
                  {timeLabel(todayRecord.jam_masuk)}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs text-slate-500">Pulang</p>
                <p className="mt-1 font-mono text-lg font-semibold text-slate-800">
                  {todayRecord.jam_keluar ? timeLabel(todayRecord.jam_keluar) : "-"}
                </p>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}